import React, { useState, useEffect, useCallback } from 'react';
import { Search, ShoppingBag, Loader2, Globe, MessageCircle, Headset } from 'lucide-react';
import api from '../../../api/axios';

const intentColors = {
  'Order Inquiry': 'bg-blue-100 text-blue-700',
  'Product Query': 'bg-violet-100 text-violet-700',
  'Delivery': 'bg-cyan-100 text-cyan-700',
  'Refund': 'bg-rose-100 text-rose-700',
  'Payment': 'bg-amber-100 text-amber-700',
  'Discount': 'bg-emerald-100 text-emerald-700',
  'Return': 'bg-orange-100 text-orange-700',
  'General': 'bg-gray-100 text-gray-600',
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' }) +
    ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });
}

const SAChatList = ({ from, to, selectedId, onSelect, refreshKey }) => {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [tab, debouncedSearch, from, to]);

  const fetchSessions = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const params = { from, to, page, size: 15 };
      if (tab !== 'all') params.status = tab;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await api.get('/api/v1/shopping-assistance/sessions', { params });
      setSessions(res.data?.data?.rows || []);
      setTotal(res.data?.data?.total || 0);
    } catch {
      setSessions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [from, to, tab, debouncedSearch, page, refreshKey]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  return (
    <div className="flex flex-col bg-white border-r border-gray-100" style={{ height: '100%' }}>
      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-3 pt-3 shrink-0">
        {['all', 'open', 'closed'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold capitalize rounded-t-lg transition-colors ${
              tab === t
                ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-3 py-2.5 shrink-0">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Search size={14} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-xs text-gray-700 placeholder-gray-400 outline-none w-full"
          />
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto divide-y divide-gray-50 flex-1">
        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <ShoppingBag size={28} className="mb-2 opacity-40" />
            <p className="text-xs">No conversations found</p>
          </div>
        )}

        {!loading && sessions.map((chat) => {
          const isActive = chat.status === 'active';
          const isWhatsApp = chat.channel === 'whatsapp';
          const displayName = isWhatsApp && chat.customerName
            ? chat.customerName
            : chat.sessionId?.slice(0, 8) || `#${chat.id}`;
          return (
            <button
              key={chat.id}
              onClick={() => onSelect(chat)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                (selectedId === chat.id || chat.sessionIds?.includes(selectedId)) ? 'bg-blue-50 border-l-2 border-blue-500' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-800">
                      {displayName}
                    </span>
                    {isWhatsApp ? (
                      <MessageCircle size={13} className="text-green-500 shrink-0" />
                    ) : (
                      <Globe size={13} className="text-blue-400 shrink-0" />
                    )}
                    {isActive && !chat.humanTakeover && (
                      <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    )}
                    {chat.humanTakeover && (
                      <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium shrink-0">
                        <Headset size={10} />
                        Human
                      </span>
                    )}
                  </div>
                  {chat.intent && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      intentColors[chat.intent] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {chat.intent}
                    </span>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5 truncate">
                    {chat.lastMessage || chat.summary || 'No messages yet'}
                  </p>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                  {formatTime(chat.startedAt)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Pagination */}
      {total > 15 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 shrink-0">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
          >
            Prev
          </button>
          <span className="text-xs text-gray-400">
            {page * 15 + 1}–{Math.min((page + 1) * 15, total)} of {total}
          </span>
          <button
            disabled={(page + 1) * 15 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SAChatList;
